// Keeps the offline copy in IndexedDB up to date.
//
// Recipe summaries (everything except steps and descriptions) are synced for
// all recipes at start-up, then incrementally. Full recipes are cached as they
// are opened (lib/recipes.ts). List changes are applied locally first and
// queued in an outbox, so they work offline and replay when back online.

import { create } from 'zustand';
import type { SummariesResponse, UserList } from '@dishly/shared';
import { ApiError, api, isNetworkError } from './api';
import { db, getMeta, setMeta } from './db';
import { currentUserId } from '../stores/session';

const SUMMARY_META = 'summarySync';
const FULL_SYNC_EVERY_MS = 24 * 60 * 60 * 1000;

interface SummarySyncMeta {
  cursor: string;
  userId: string | null;
  fullAt: number;
}

interface SyncStatus {
  /** A summaries sync is running. */
  syncing: boolean;
  /** At least one sync has completed on this device. */
  hasSynced: boolean;
  lastError: string | null;
}

export const useSyncStatus = create<SyncStatus>(() => ({ syncing: false, hasSynced: false, lastError: null }));

void getMeta<SummarySyncMeta>(SUMMARY_META).then((meta) => {
  if (meta) useSyncStatus.setState({ hasSynced: true });
});

let summariesRun: Promise<void> | null = null;

export function syncSummaries(options: { full?: boolean } = {}): Promise<void> {
  summariesRun ??= (async () => {
    useSyncStatus.setState({ syncing: true });
    let userChanged = false;
    try {
      const userId = currentUserId();
      const meta = await getMeta<SummarySyncMeta>(SUMMARY_META);
      const full = options.full || !meta || meta.userId !== userId || Date.now() - meta.fullAt > FULL_SYNC_EVERY_MS;
      const query = full || !meta ? '' : `?since=${encodeURIComponent(meta.cursor)}`;
      const response = await api<SummariesResponse>(`/recipes/summaries${query}`);
      if (currentUserId() !== userId) {
        // Signed in or out while this was running: sync again for the new user.
        userChanged = true;
        return;
      }

      await db.transaction('rw', [db.summaries, db.meta, db.recipes], async () => {
        if (response.full) {
          await db.summaries.clear();
          // Drop cached recipes that are no longer visible.
          const visible = new Set(response.recipes.map((r) => r.id));
          await db.recipes.filter((r) => !visible.has(r.id)).delete();
        }
        if (response.recipes.length) await db.summaries.bulkPut(response.recipes);
        if (response.removed.length) {
          await db.summaries.bulkDelete(response.removed);
          await db.recipes.bulkDelete(response.removed);
        }
        await setMeta(SUMMARY_META, {
          cursor: response.cursor,
          userId,
          fullAt: response.full ? Date.now() : (meta?.fullAt ?? Date.now()),
        } satisfies SummarySyncMeta);
      });
      useSyncStatus.setState({ hasSynced: true, lastError: null });
    } catch (error) {
      useSyncStatus.setState({ lastError: error instanceof Error ? error.message : String(error) });
      if (!isNetworkError(error)) console.error('[sync] summaries', error);
    } finally {
      useSyncStatus.setState({ syncing: false });
      summariesRun = null;
      if (userChanged) void syncSummaries();
    }
  })();
  return summariesRun;
}

let outboxRun: Promise<void> | null = null;
let retryTimer = 0;

/**
 * The server can be unreachable while the browser still thinks it is online
 * (so no "online" event will come): retry queued changes on a timer as well.
 */
function scheduleOutboxRetry(): void {
  if (retryTimer) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = 0;
    void syncLists();
  }, 30_000);
}

/** Sends queued list changes in order. Stops at the first network failure and retries later. */
export function flushOutbox(): Promise<void> {
  outboxRun ??= (async () => {
    let rejected = false;
    try {
      for (;;) {
        const entry = await db.outbox.orderBy('seq').first();
        if (!entry) break;
        try {
          await api(entry.path, { method: entry.method, body: entry.body });
        } catch (error) {
          if (isNetworkError(error) || (error instanceof ApiError && (error.status >= 500 || error.status === 429))) {
            scheduleOutboxRetry();
            return;
          }
          // The server refused it (e.g. the recipe was deleted): drop it and reconcile below.
          rejected = true;
        }
        await db.outbox.delete(entry.seq!);
      }
    } finally {
      outboxRun = null;
    }
    if (rejected) await fetchLists();
  })();
  return outboxRun;
}

async function fetchLists(): Promise<void> {
  const userId = currentUserId();
  if (!userId) return;
  const { lists } = await api<{ lists: UserList[] }>('/lists');
  if (currentUserId() !== userId || (await db.outbox.count()) > 0) return;
  await db.transaction('rw', db.lists, async () => {
    await db.lists.clear();
    await db.lists.bulkPut(lists);
  });
}

export async function syncLists(): Promise<void> {
  if (!currentUserId()) {
    await db.lists.clear();
    return;
  }
  try {
    await flushOutbox();
    // While changes are still queued (offline), keep the optimistic local copy.
    if ((await db.outbox.count()) === 0) await fetchLists();
  } catch (error) {
    if (!isNetworkError(error)) console.error('[sync] lists', error);
  }
}

let lastRun = 0;

export function syncNow(): void {
  lastRun = Date.now();
  void syncSummaries();
  void syncLists();
}

export function startBackgroundSync(): void {
  syncNow();
  window.addEventListener('online', syncNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - lastRun > 60_000) syncNow();
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') syncNow();
  }, 5 * 60_000);
}

/** Forgets everything tied to the signed-in user (on sign-out or account switch). */
export async function clearUserData(): Promise<void> {
  await db.transaction('rw', [db.lists, db.outbox, db.listTicks, db.drafts, db.adaptations, db.recipes, db.meta], async () => {
    await Promise.all([db.lists.clear(), db.outbox.clear(), db.listTicks.clear(), db.drafts.clear(), db.adaptations.clear()]);
    await db.recipes.filter((r) => r.isDraft || r.visibility !== 'public').delete();
    await db.meta.delete(SUMMARY_META);
  });
}
